import { useSelector } from 'react-redux';
import { selectAppliedOffers } from '../../store/slices/OfferSlice';
import useBasket from '../../hooks/basket/useBasket';
import useOffersTranslations from '../offers/hooks/useOffersTranslations';
import { Offer } from '../../services/exports/Interfaces';

export interface ActiveOfferEntry {
  id: number;
  label: string;
  /** € this offer saves on the current basket. */
  value: number;
}

export interface ActiveOffersSummary {
  /** The active offers, sorted by € saving (highest first). */
  offers: ActiveOfferEntry[];
  count: number;
  totalValue: number;
}

/**
 * FOO-2555 / PROD-407 (UX feedback item 6) — summarise the offers currently in play so the
 * exclusivity surfaces can say "your {n} active offers ({total})" instead of naming just one.
 *
 * Pure so the displacement notice (whose "active" offers are the *suppressed* ones, not the
 * applied ones) can reuse the same shaping with a different source list.
 */
export function summarizeOffers(
  offers: Offer[] | undefined,
  previewOfferSaving: (offer: Offer) => number,
  getLabelByOffer: (offer: Offer) => string,
): ActiveOffersSummary {
  const entries: ActiveOfferEntry[] = (offers ?? [])
    .filter(Boolean)
    .map((offer) => ({ id: offer.id, label: getLabelByOffer(offer), value: previewOfferSaving(offer) || 0 }))
    .sort((a, b) => b.value - a.value);

  return { offers: entries, count: entries.length, totalValue: entries.reduce((sum, e) => sum + e.value, 0) };
}

/**
 * The offers currently APPLIED to the order (the pre-displacement view used by the promo/gift
 * card + modal comparison). While a promo/gift is active these are suppressed to empty — the
 * displacement notice instead summarises the offers it removed via {@link summarizeOffers}.
 */
export default function useActiveOffers(): ActiveOffersSummary {
  const appliedOffers = useSelector(selectAppliedOffers);
  const { previewOfferSaving } = useBasket();
  const { getLabelByOffer } = useOffersTranslations();

  return summarizeOffers(appliedOffers, previewOfferSaving, getLabelByOffer);
}
